import React, { useState, memo } from 'react';
import { MessageSquare, Search, Sparkles, Heart, Briefcase, Home, Utensils, ChevronRight } from 'lucide-react';
import { EmptyState } from './EmptyState';
type PromptCategory = 'all' | 'childhood' | 'family' | 'career' | 'traditions' | 'recipes';
type Prompt = {
  id: number;
  category: PromptCategory;
  text: string;
  icon: React.ReactNode;
};
const prompts: Prompt[] = [{
  id: 1,
  category: 'childhood',
  text: 'What was your favorite toy as a child?',
  icon: '🧸'
}, {
  id: 2,
  category: 'childhood',
  text: 'Describe your childhood home and neighborhood',
  icon: '🏡'
}, {
  id: 3,
  category: 'childhood',
  text: 'What was your first day of school like?',
  icon: '🎒'
}, {
  id: 4,
  category: 'family',
  text: 'Tell us about how you met your spouse',
  icon: '💑'
}, {
  id: 5,
  category: 'family',
  text: 'What family traditions are most important to you?',
  icon: '🎄'
}, {
  id: 6,
  category: 'family',
  text: 'Describe a memorable family vacation',
  icon: '✈️'
}, {
  id: 7,
  category: 'career',
  text: 'What was your first job?',
  icon: '💼'
}, {
  id: 8,
  category: 'career',
  text: "Tell us about a career achievement you're proud of",
  icon: '🏆'
}, {
  id: 9,
  category: 'career',
  text: 'What advice would you give to someone starting in your field?',
  icon: '💡'
}, {
  id: 10,
  category: 'traditions',
  text: 'How does your family celebrate holidays?',
  icon: '🎉'
}, {
  id: 11,
  category: 'traditions',
  text: 'What cultural traditions have you passed down?',
  icon: '🌍'
}, {
  id: 12,
  category: 'recipes',
  text: 'Share your favorite family recipe',
  icon: '🍳'
}, {
  id: 13,
  category: 'recipes',
  text: 'What meal reminds you of home?',
  icon: '🍲'
}];
type PromptsLibraryProps = {
  onSelectPrompt: (prompt: string) => void;
};
export function PromptsLibrary({
  onSelectPrompt
}: PromptsLibraryProps) {
  const [selectedCategory, setSelectedCategory] = useState<PromptCategory>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const categories = [{
    id: 'all' as PromptCategory,
    label: 'All Prompts',
    icon: <Sparkles className="w-4 h-4" data-id="element-1427" />
  }, {
    id: 'childhood' as PromptCategory,
    label: 'Childhood',
    icon: <Heart className="w-4 h-4" data-id="element-1428" />
  }, {
    id: 'family' as PromptCategory,
    label: 'Family',
    icon: <Home className="w-4 h-4" data-id="element-1429" />
  }, {
    id: 'career' as PromptCategory,
    label: 'Career',
    icon: <Briefcase className="w-4 h-4" data-id="element-1430" />
  }, {
    id: 'traditions' as PromptCategory,
    label: 'Traditions',
    icon: <Sparkles className="w-4 h-4" data-id="element-1431" />
  }, {
    id: 'recipes' as PromptCategory,
    label: 'Recipes',
    icon: <Utensils className="w-4 h-4" data-id="element-1432" />
  }];
  const filteredPrompts = prompts.filter(prompt => {
    const matchesCategory = selectedCategory === 'all' || prompt.category === selectedCategory;
    const matchesSearch = prompt.text.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  return <div className="flex-1 bg-gray-100 min-h-screen" data-id="element-1433">
      <div className="px-8 py-6" data-id="element-1434">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6" data-id="element-1435">
          <div className="p-2 bg-blue-100 rounded-lg" data-id="element-1436">
            <MessageSquare className="w-6 h-6 text-blue-600" data-id="element-1437" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-1438">Story Prompts</h1>
        </div>

        <p className="text-gray-600 mb-6" data-id="element-1439">
          Need inspiration? Choose from our curated prompts to help you tell
          your story.
        </p>

        {/* Search */}
        <div className="mb-6" data-id="element-1440">
          <div className="relative max-w-md" data-id="element-1441">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1442" />
            <input type="text" placeholder="Search prompts..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-400 focus:outline-none transition-colors" data-id="element-1443" />
          </div>
        </div>

        {/* Categories */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2" data-id="element-1444">
          {categories.map(category => <button key={category.id} onClick={() => setSelectedCategory(category.id)} className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-colors ${selectedCategory === category.id ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-1445">
              {category.icon}
              {category.label}
            </button>)}
        </div>

        {/* Prompts Grid */}
        {filteredPrompts.length === 0 ? <EmptyState icon={Search} title="No prompts found" description={searchQuery ? `No prompts match "${searchQuery}"` : 'No prompts in this category'} data-id="element-1446" /> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1447">
            {filteredPrompts.map(prompt => <button key={prompt.id} onClick={() => onSelectPrompt(prompt.text)} className="bg-white rounded-xl p-6 text-left hover:shadow-md transition-shadow group" data-id="element-1448">
                <div className="flex items-start justify-between mb-3" data-id="element-1449">
                  <span className="text-3xl" data-id="element-1450">{prompt.icon}</span>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-green-600 transition-colors" data-id="element-1451" />
                </div>
                <p className="text-gray-900 font-medium leading-relaxed" data-id="element-1452">
                  {prompt.text}
                </p>
              </button>)}
          </div>}
      </div>
    </div>;
}